import React, { useState } from "react"

import * as ModalModule from "../../shared/Modal"
import TypographyJs from "../../shared/Typography"

type AnyProps = Record<string, unknown> & { children?: React.ReactNode }
const AnimatedModal = ModalModule.AnimatedModal as unknown as React.FC<AnyProps>
const Typography = TypographyJs as unknown as React.FC<AnyProps>

export type PackageSessionConfirmAction = "markOne" | "markFinished" | "undo"

type Props = {
  visible: boolean
  action: PackageSessionConfirmAction | null
  packageName?: string | null
  onClose: () => void
  onConfirm: () => Promise<void> | void
}

const COPY: Record<
  PackageSessionConfirmAction,
  { header: string; body: (name: string) => string; confirmLabel: string; confirmType: "primary" | "warning" }
> = {
  markOne: {
    header: "Mark session as used",
    body: (name) => `Mark one session of "${name}" as used? This will reduce remaining sessions.`,
    confirmLabel: "Mark session used",
    confirmType: "primary"
  },
  markFinished: {
    header: "Mark package as finished",
    body: (name) =>
      `Mark "${name}" as finished? All remaining sessions will be used and the package will close.`,
    confirmLabel: "Mark package finished",
    confirmType: "warning"
  },
  undo: {
    header: "Undo marked session",
    body: () => "Undo this manually marked session? It will be restored to remaining sessions.",
    confirmLabel: "Undo",
    confirmType: "warning"
  }
}

export default function PackageSessionConfirmModal({
  visible,
  action,
  packageName,
  onClose,
  onConfirm
}: Props) {
  const [loading, setLoading] = useState(false)
  const name = packageName || "this package"
  const copy = action ? COPY[action] : null

  const handleConfirm = async () => {
    setLoading(true)
    try {
      await onConfirm()
    } finally {
      setLoading(false)
    }
  }

  if (!copy) return null

  return (
    <AnimatedModal
      visible={visible}
      hideModal={onClose}
      header={copy.header}
      showFooter
      hideSaveButton
      hideCancelButton
      footerButtons={[
        {
          label: "Go back",
          type: "tertiary",
          onClick: onClose,
          disabled: loading
        },
        {
          label: loading ? "Working…" : copy.confirmLabel,
          type: copy.confirmType,
          onClick: handleConfirm,
          disabled: loading
        }
      ]}>
      <Typography variant="body" as="p">
        {copy.body(name)}
      </Typography>
    </AnimatedModal>
  )
}
